import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface MonthlyLead {
  month: number;
  total: number;
}

export default function MonthlySalesChart() {
  const [monthlyLeads, setMonthlyLeads] = useState<number[]>(Array(12).fill(0));
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMonthlyLeads = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token) {
        setError("Authorization required. Please log in.");
        navigate("/signin");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard-summary`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError("Authorization required. Please log in.");
            navigate("/signin");
            return;
          }
          throw new Error('Failed to fetch data');
        }

        const data = await response.json();
        const monthlyData = data.data.monthly_leads;

        // Initialize array with 0s for all 12 months
        const leadsByMonth = Array(12).fill(0);

        // Map API data to correct month indices (1-12 to 0-11)
        monthlyData.forEach((item: MonthlyLead) => {
          if (item.month >= 1 && item.month <= 12) {
            leadsByMonth[item.month - 1] = item.total;
          }
        });

        setMonthlyLeads(leadsByMonth);
      } catch (error) {
        console.error('Error fetching monthly leads:', error);
        setError('Failed to fetch data. Please try again.');
      }
    };

    fetchMonthlyLeads();
  }, [navigate]);

  const options: ApexOptions = {
    colors: ["#465fff"],
    chart: {
      fontFamily: "Outfit, sans-serif",
      type: "bar",
      height: 300,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "39%",
        borderRadius: 5,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 4,
      colors: ["transparent"],
    },
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontFamily: "Outfit",
    },
    yaxis: {
      title: {
        text: undefined,
      },
    },
    grid: {
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      x: {
        show: false,
      },
      y: {
        formatter: (val: number) => `${val}`,
      },
    },
  };

  const series = [
    {
      name: "Leads",
      data: monthlyLeads,
    },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-5 pt-5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 sm:pt-6">
      {error && (
        <div className="text-red-500 text-sm mb-4">{error}</div>
      )}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
          Leads by Month
        </h3>
        <div className="relative inline-block">
          <button
            onClick={() => navigate("/lead-list")}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 px-3 py-1 rounded-md text-sm font-medium"
          >
            View More
          </button>
        </div>
      </div>

      <div className="max-w-full overflow-x-auto custom-scrollbar">
        <div className="-ml-5 min-w-[650px] xl:min-w-full pl-2">
          <Chart options={options} series={series} type="bar" height={300} />
        </div>
      </div>
    </div>
  );
}